import React, { useEffect, useMemo, useState } from "react";
import { DashboardNavbar } from "@/components/dashboard/DashboardNavbar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { createBooking } from "@/utils/bookingDatabase";
import { useClientNotifications } from "@/hooks/useClientNotifications";
import { Badge } from "@/components/ui/badge";
import { formatTime } from "@/utils/dateFormatting";
import { FullPageTableSkeleton } from "@/components/ui/skeleton-block";

type BookingRow = {
  id: string;
  booking_date: string; // yyyy-MM-dd
  booking_time: string; // HH:mm:ss
  duration: number;
  status: string;
  client_name: string;
  service_name: string | null;
  client_id?: string;
  client_phone?: string | null;
  no_show_status?: string | null;
  payment_status?: string | null;
};

function formatDisplayDate(isoDate: string): string {
  try {
    const d = new Date(isoDate + "T00:00:00");
    return d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return isoDate;
  }
}

const BookingsPage: React.FC = () => {
  const [bookings, setBookings] = useState<BookingRow[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [cancelling, setCancelling] = useState<string | null>(null);
  const [businessId, setBusinessId] = useState<string | null>(null);

  // Create Booking dialog state
  const [createOpen, setCreateOpen] = useState(false);
  const [clients, setClients] = useState<{ id: string; name: string }[]>([]);
  const [services, setServices] = useState<{ id: string; name: string }[]>([]);
  const [newClientId, setNewClientId] = useState<string>("");
  const [newServiceId, setNewServiceId] = useState<string>("");
  const [newDate, setNewDate] = useState<string>(""); // yyyy-MM-dd
  const [newTime, setNewTime] = useState<string>(""); // HH:mm
  const [newDuration, setNewDuration] = useState<number>(60);
  const [creating, setCreating] = useState(false);

  // Edit Booking dialog state
  const [editOpen, setEditOpen] = useState(false);
  const [editBookingId, setEditBookingId] = useState<string | null>(null);
  const [editDate, setEditDate] = useState<string>("");
  const [editTime, setEditTime] = useState<string>("");
  const [editDuration, setEditDuration] = useState<number>(60);
  const [editStatus, setEditStatus] = useState<string>("confirmed");
  const [savingEdit, setSavingEdit] = useState(false);
  const { sendAppointmentReminder, isSending } = useClientNotifications();

  const confirmedCount = useMemo(() => bookings.filter(b => b.status === "confirmed").length, [bookings]);

  useEffect(() => {
    document.title = "Bookings | Figgy.link";
    loadBookings();
  }, []);

  const loadBookings = async () => {
    try {
      setLoading(true);
      // Resolve business profile id for current authenticated user
      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        setBookings([]);
        setLoading(false);
        toast.error('Not authenticated');
        return;
      }

      const { data: biz } = await supabase
        .from('business_profiles')
        .select('id')
        .eq('user_id', user.id)
        .maybeSingle();

      if (!biz?.id) {
        setBookings([]);
        setLoading(false);
        toast.error('Could not resolve business profile');
        return;
      }

      setBusinessId(biz.id);

      const { data, error } = await supabase
        .from('bookings')
        .select(`
          id,
          booking_date,
          booking_time,
          duration,
          status,
          payment_status,
          no_show_status,
          service_name,
          customer_name,
          customer_phone,
          client_id
        `)
        .eq('business_id', biz.id)
        .order('booking_date', { ascending: false })
        .order('booking_time', { ascending: false });

      if (error) {
        console.warn('Bookings query error:', error.message);
      }

      const rows: BookingRow[] = (data || []).map((b: any) => ({
        id: b.id,
        booking_date: b.booking_date,
        booking_time: b.booking_time,
        duration: b.duration,
        status: b.status,
        client_name: b.customer_name || '',
        service_name: b.service_name ?? null,
        client_id: b.client_id ?? null,
        client_phone: b.customer_phone ?? null,
        no_show_status: b.no_show_status ?? null,
        payment_status: b.payment_status ?? null,
      }));

      setBookings(rows);

      // Preload clients/services for creation
      const [{ data: clientRows }, { data: serviceRows }] = await Promise.all([
        supabase
          .from('clients')
          .select('id, first_name, last_name')
          .eq('user_id', biz.id)
          .order('first_name'),
        supabase
          .from('business_services')
          .select('id, name')
          .eq('business_id', biz.id)
          .order('name'),
      ]);
      setClients((clientRows || []).map(c => ({ id: c.id, name: `${c.first_name ?? ''} ${c.last_name ?? ''}`.trim() })));
      setServices((serviceRows || []).map(s => ({ id: s.id, name: s.name })));
    } catch (e: any) {
      console.error(e);
      toast.error(e?.message || 'Failed to load bookings');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelBooking = async (bookingId: string) => {
    try {
      setCancelling(bookingId);

      const { cancelBooking } = await import('@/services/bookingActions');
      const result = await cancelBooking({
        bookingId,
        mode: 'admin',
      });

      if (!result.success) {
        throw new Error(result.error || 'Failed to cancel booking');
      }

      setBookings(prev => prev.map(b => b.id === bookingId ? { ...b, status: 'cancelled' } : b));
      toast.success('Booking cancelled');
    } catch (e: any) {
      console.error(e);
      toast.error(e?.message || 'Failed to cancel booking');
    } finally {
      setCancelling(null);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <DashboardNavbar />
      <div className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-between mb-8">
            <h1 className="text-2xl font-semibold">Bookings</h1>
            <div className="text-sm text-muted-foreground">{confirmedCount} confirmed</div>
          </div>

          {loading ? <FullPageTableSkeleton rows={6} cols={7} /> : (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>All Appointments</CardTitle>
                <Button className="bg-figgy-500 hover:bg-figgy-600" onClick={() => setCreateOpen(true)}>
                  New Booking
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {bookings.length === 0 ? (
                <div className="py-6 text-center text-sm text-muted-foreground">No bookings yet</div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Time</TableHead>
                        <TableHead>Client</TableHead>
                        <TableHead>Service</TableHead>
                        <TableHead>Duration</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {bookings.map(b => (
                        <TableRow key={b.id}>
                          <TableCell>{formatDisplayDate(b.booking_date)}</TableCell>
                          <TableCell>{formatTime(b.booking_time)}</TableCell>
                          <TableCell className="font-medium">{b.client_name || '—'}</TableCell>
                          <TableCell>{b.service_name || '—'}</TableCell>
                          <TableCell>{b.duration} min</TableCell>
                          <TableCell className="capitalize">
                            <div className="flex gap-2 items-center">
                              <Badge variant={b.status === 'confirmed' ? 'default' : b.status === 'cancelled' ? 'secondary' : 'outline'} className="text-[10px]">
                                {b.status}
                              </Badge>
                              {b.no_show_status && (
                                <Badge variant={b.no_show_status === 'charged' ? 'destructive' : 'secondary'} className="text-[10px]">
                                  no-show: {b.no_show_status}
                                </Badge>
                              )}
                              {b.payment_status && (
                                <Badge variant={b.payment_status === 'paid' ? 'default' : b.payment_status === 'failed' ? 'destructive' : 'secondary'} className="text-[10px]">
                                  payment: {b.payment_status}
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleCancelBooking(b.id)}
                                disabled={b.status !== 'confirmed' || cancelling === b.id || b.payment_status === 'paid'}
                              >
                                {cancelling === b.id ? 'Cancelling…' : 'Cancel'}
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                disabled={isSending || !b.client_id || !b.client_phone || b.status !== 'confirmed'}
                                onClick={async () => {
                                  try {
                                    // Build appointment Date from booking_date + booking_time
                                    const date = new Date(`${b.booking_date}T${b.booking_time}`);
                                    await sendAppointmentReminder(
                                      b.client_id as string,
                                      b.client_phone as string,
                                      date,
                                      undefined,
                                      undefined,
                                      b.id
                                    );
                                  } catch (e: any) {
                                    console.error(e);
                                  }
                                }}
                              >
                                {isSending ? 'Sending…' : 'SMS Reminder'}
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                disabled={b.status !== 'confirmed' || !!b.no_show_status}
                                onClick={async () => {
                                  try {
                                    const { data, error } = await supabase.functions.invoke('process-no-show-fee', {
                                      body: { bookingId: b.id }
                                    });
                                    if (error) throw error;
                                    const msg = data?.message || (data?.warning ? 'Warning issued for first no-show' : 'Marked as no-show');
                                    toast.success(msg);
                                    await loadBookings();
                                  } catch (e: any) {
                                    console.error(e);
                                    toast.error(e?.message || 'Failed to mark no-show');
                                  }
                                }}
                              >
                                Mark No-Show
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setEditBookingId(b.id);
                                  setEditDate(b.booking_date);
                                  // convert HH:mm:ss to HH:mm
                                  setEditTime(b.booking_time.slice(0,5));
                                  setEditDuration(b.duration);
                                  setEditStatus(b.status);
                                  setEditOpen(true);
                                }}
                              >
                                Edit
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
          )}
        </div>
      </div>

      {/* Create Booking Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New Booking</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Client</Label>
              <Select value={newClientId} onValueChange={setNewClientId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select client" />
                </SelectTrigger>
                <SelectContent>
                  {clients.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name || 'Unnamed'}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Service</Label>
              <Select value={newServiceId} onValueChange={setNewServiceId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select service" />
                </SelectTrigger>
                <SelectContent>
                  {services.map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Date</Label>
                <Input type="date" value={newDate} onChange={(e) => setNewDate(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label>Time</Label>
                <Input type="time" value={newTime} onChange={(e) => setNewTime(e.target.value)} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Duration (min)</Label>
              <Input type="number" min={15} step={15} value={newDuration} onChange={(e) => setNewDuration(parseInt(e.target.value || '60', 10))} />
            </div>
            <div className="pt-2 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setCreateOpen(false)}>Close</Button>
              <Button
                className="bg-figgy-500 hover:bg-figgy-600"
                disabled={creating}
                onClick={async () => {
                  try {
                    if (!businessId) { toast.error('Missing business'); return; }
                    if (!newClientId || !newServiceId || !newDate || !newTime) { toast.error('Fill all fields'); return; }
                    setCreating(true);
                    // Convert 24h HH:mm to display AM/PM for createBooking
                    const [hhStr, mmStr] = newTime.split(':');
                    const hh = parseInt(hhStr, 10);
                    const period = hh >= 12 ? 'PM' : 'AM';
                    const hour12 = hh === 0 ? 12 : hh > 12 ? hh - 12 : hh;
                    const displayTime = `${hour12.toString().padStart(2,'0')}:${mmStr} ${period}`;

                    const result = await createBooking({
                      businessId,
                      serviceId: newServiceId,
                      clientId: newClientId,
                      bookingDate: new Date(newDate),
                      bookingTime: displayTime,
                      duration: newDuration,
                    });
                    if (!result.success) {
                      toast.error(result.error || 'Failed to create booking');
                      return;
                    }
                    toast.success('Booking created');
                    setCreateOpen(false);
                    // reset form
                    setNewClientId("");
                    setNewServiceId("");
                    setNewDate("");
                    setNewTime("");
                    setNewDuration(60);
                    await loadBookings();
                  } catch (e: any) {
                    console.error(e);
                    toast.error(e?.message || 'Failed to create booking');
                  } finally {
                    setCreating(false);
                  }
                }}
              >
                {creating ? 'Creating…' : 'Create Booking'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Booking Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Booking</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Date</Label>
                <Input type="date" value={editDate} onChange={(e) => setEditDate(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label>Time</Label>
                <Input type="time" value={editTime} onChange={(e) => setEditTime(e.target.value)} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Duration (min)</Label>
                <Input type="number" min={15} step={15} value={editDuration} onChange={(e) => setEditDuration(parseInt(e.target.value || '60', 10))} />
              </div>
              <div className="space-y-1.5">
                <Label>Status</Label>
                <Select value={editStatus} onValueChange={setEditStatus}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="confirmed">confirmed</SelectItem>
                    <SelectItem value="pending">pending</SelectItem>
                    <SelectItem value="cancelled">cancelled</SelectItem>
                    <SelectItem value="completed">completed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="pt-2 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setEditOpen(false)}>Close</Button>
              <Button
                className="bg-figgy-500 hover:bg-figgy-600"
                disabled={savingEdit || !editBookingId}
                onClick={async () => {
                  try {
                    if (!editBookingId) return;
                    setSavingEdit(true);
                    // convert time HH:mm to HH:mm:ss for DB
                    const dbTime = `${editTime}:00`;
                    const { error } = await supabase
                      .from('bookings')
                      .update({
                        booking_date: editDate,
                        booking_time: dbTime,
                        duration: editDuration,
                        status: editStatus,
                        updated_at: new Date().toISOString(),
                      })
                      .eq('id', editBookingId);
                    if (error) throw error;
                    toast.success('Booking updated');
                    setEditOpen(false);
                    await loadBookings();
                  } catch (e: any) {
                    console.error(e);
                    toast.error(e?.message || 'Failed to update booking');
                  } finally {
                    setSavingEdit(false);
                  }
                }}
              >
                {savingEdit ? 'Saving…' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BookingsPage;


